import * as THREE from 'three/webgpu';

import {
    FeatureFace,
    FeatureLine,
    FeaturePoint,
    isFeatureFace,
    isFeatureLine,
    isFeaturePoint,
    isSketchContour,
    SketchCollectionObject,
    type SketchContour,
} from '../../mesh';
import { FeatureResultMesh, isFeatureResultMesh } from '../../mesh/feature/FeatureResultMesh';
import { isRevolveRegionContourObject, type RevolveRegionContourObject } from '../../mesh/feature/revolve';
import {
    isAbstractSketchEdgeObject,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    isSketchPointBoxSelectable,
    isSketchPointEntitySelectable,
    SketchLineObject,
} from '../../mesh/sketch';
import {
    isEdgeAwareSelectionMode,
    type EntitySelectionMode,
} from './EntitySelectionMode';
import {
    resolvePrimaryEntityHit,
    resolvePrimarySketchHit,
    resolveSelectionDomainWinner,
} from './SelectionHitArbitration';
import type { EntitySelectionManagerRuntime } from './EntitySelectionRuntime';
import { findAncestorSketchGroupId } from '../SketchEditingInteractionGuard';
import { isObjectInsideFeaturePreviewMesh } from '../../utils/feature';
import { isSketchEntityReferencePreview } from '../../reference/sketch-entity-reference/command/preview/SketchEntityReferencePreviewMarker';
import {
    isEntitySelectionCandidateMaskAllowed,
    resolveEntitySelectionCandidateMask,
} from './utils/entitySelectionCandidateMask';

/**
 * 允许参与选择与命中判断的草图/特征对象守卫列表。
 *
 * 说明：保持与原实现顺序一致，避免命中时的优先级发生变化。
 */
export const ENTITY_SELECTION_STATE_OBJECT_GUARDS: Array<(value: THREE.Object3D) => boolean> = [
    isSketchPointObject,
    isSketchLineObject,
    isSketchCircleObject,
    isSketchArcObject,
    isSketchEllipseObject,
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    isSketchContour,
    isRevolveRegionContourObject,
    isSketchCollectionObject,
    isFeaturePoint,
    isFeatureFace,
    isFeatureLine,
    isSketchSlotObject,
    (value: THREE.Object3D): value is FeatureResultMesh => isFeatureResultMesh(value),
];

/**
 * 判断对象是否应从实体选择候选集中排除。
 *
 * 设计说明：
 * 1. 隐藏对象以及隐藏父级下的对象不应继续参与真实选择，否则隐藏草图会抢占共面实体面；
 * 2. 预览体及其子树只负责显示，不得重新进入实体选择；
 * 3. 该规则必须在 cad-core 选择层统一生效，禁止 web-client 自行做命中特判；
 * 4. 后续所有特征的 preview object 都可沿用这条底层约束。
 * 5. 可见模型边动态会话参考点只属于点捕捉域，不得进入实体点击、悬停或框选链路。
 *
 * @param object 场景对象。
 * @returns 是否应排除。
 */
function shouldIgnoreInEntitySelection(object: THREE.Object3D): boolean {
    return !isObjectVisibleInSceneTree(object)
        || object.userData?.skipSelection === true
        || isObjectInsideFeaturePreviewMesh(object)
        || isSketchEntityReferencePreview(object)
        || isDrawingPreviewSketchObject(object)
        || (isSketchPointObject(object) && !isSketchPointEntitySelectable(object));
}

/**
 * 判断当前对象及其子树是否应整体跳过实体选择候选收集。
 *
 * @param object 场景对象。
 * @returns 是否跳过整棵子树。
 */
function shouldSkipEntitySelectionSubtree(object: THREE.Object3D): boolean {
    return object.userData?.skipSelectionSubtree === true;
}

/**
 * 判断对象是否处于显式跳过实体选择的子树中。
 *
 * 稳定候选索引不再每次从场景根递归，因此必须沿当前父链实时验证该语义，
 * 才能保持预览组或临时辅助组动态切换后的选择行为与完整遍历一致。
 *
 * @param object 待验证对象。
 * @returns 对象自身或任一祖先要求跳过整棵子树时返回 true。
 */
function isInsideSkippedEntitySelectionSubtree(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object;
    while (current) {
        if (shouldSkipEntitySelectionSubtree(current)) return true;
        current = current.parent;
    }
    return false;
}

/**
 * 遍历实体选择候选树。
 *
 * @param root 遍历根节点。
 * @param visitor 候选访问回调。
 */
function traverseEntitySelectionTree(root: THREE.Object3D, visitor: (object: THREE.Object3D) => void): void {
    const pendingObjects: THREE.Object3D[] = [root];
    while (pendingObjects.length > 0) {
        const object = pendingObjects.pop()!;
        if (shouldSkipEntitySelectionSubtree(object)) {
            continue;
        }

        visitor(object);
        /**
         * 使用显式栈替代 `Object3D.traverse`，让导入拓扑 pick host 可以整棵跳过。
         * 这避免默认黑边已分批挂载后，每次 hover 仍遍历所有 FeatureLine。
         */
        for (let index = object.children.length - 1; index >= 0; index -= 1) {
            pendingObjects.push(object.children[index]!);
        }
    }
}

/**
 * 判断对象是否为草图绘制过程中的临时预览对象。
 *
 * 设计说明：
 * 1. 绘制预览点/线/圆弧只服务当前绘制工具的视觉反馈；
 * 2. 它们不能进入统一选择候选，否则默认选择链迁移到底层 helper 后会把绘制临时对象当作正式草图实体；
 * 3. Convert Entities preview 走独立 marker 过滤，这里只负责兼容现有绘制态预览标记；
 * 4. 与隐藏对象过滤放在同一入口，让所有选择模式都共享一致生命周期规则。
 *
 * @param object 场景对象。
 * @returns 是否应作为绘制预览对象排除。
 */
function isDrawingPreviewSketchObject(object: THREE.Object3D): boolean {
    if (isSketchPointObject(object) && object.isDrawingPreviewPoint) {
        return true;
    }
    if (isSketchLineObject(object) && object.isDrawingPreviewLine) {
        return true;
    }
    return isSketchArcObject(object) && object.isDrawingPreviewArc;
}

/**
 * 判断对象以及其父级链路是否整体可见。
 *
 * 设计说明：
 * 1. 拉伸确认后草图组会被隐藏，但 Three.js 的 traverse 仍会遍历隐藏子树；
 * 2. 如果隐藏草图继续进入候选集，会在与拉伸底面共面时抢占实体面命中；
 * 3. 特征点默认用透明材质承载“可拾取但不常驻显示”的语义，不能因自身 visible=false 被误过滤；
 * 4. 因此可见性必须在 cad-core 选择候选层统一收口，而不是由各个命令宿主分别过滤。
 *
 * @param object 场景对象。
 * @returns 对象是否位于完整可见的父级链路中。
 */
function isObjectVisibleInSceneTree(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object;
    while (current) {
        if (!current.visible) {
            if (current === object && isFeaturePoint(object)) {
                current = current.parent;
                continue;
            }
            return false;
        }
        current = current.parent;
    }
    return true;
}

/**
 * 判断对象是否属于点击与 Hover 共用的稳定实体候选类型。
 *
 * 该判断只依赖对象运行时类型，不读取显隐、预览或父级状态，适合在成员进入场景时执行一次。
 * 动态资格必须继续调用 `isEntitySelectionCandidateEligible()`。
 *
 * @param object 场景对象。
 * @returns 是否属于实体选择候选类型。
 */
export function isEntitySelectionCandidateObject(object: THREE.Object3D): boolean {
    return isFeaturePoint(object)
        || isFeatureLine(object)
        || isFeatureFace(object)
        || isFeatureResultMesh(object)
        || isSketchPointObject(object)
        || isSketchLineObject(object)
        || isSketchCircleObject(object)
        || isSketchArcObject(object)
        || isSketchEllipseObject(object)
        || isSketchParabolaObject(object)
        || isSketchSplineObject(object)
        || isSketchStyleSplineObject(object)
        || isSketchContour(object)
        || isRevolveRegionContourObject(object);
}

/**
 * 判断稳定实体候选按当前场景状态是否仍可参与查询。
 *
 * @param object 已通过稳定类型分类的候选。
 * @returns 当前可见、非预览、非跳过子树并满足点选择规则时返回 true。
 */
export function isEntitySelectionCandidateEligible(object: THREE.Object3D): boolean {
    return !isInsideSkippedEntitySelectionSubtree(object)
        && !shouldIgnoreInEntitySelection(object);
}

/**
 * 判断稳定实体候选是否属于框选支持的类型范围。
 *
 * @param object 已通过稳定类型分类的候选。
 * @returns 是否允许进入框选几何测试。
 */
export function isBoxSelectionCandidateObject(object: THREE.Object3D): boolean {
    if (isFeaturePoint(object) || isFeatureLine(object) || isFeatureFace(object) || isFeatureResultMesh(object)) {
        return true;
    }
    if (isSketchPointObject(object)) return isSketchPointBoxSelectable(object);
    return isSketchLineObject(object)
        || isSketchCircleObject(object)
        || isSketchArcObject(object)
        || isSketchEllipseObject(object)
        || isSketchParabolaObject(object)
        || isSketchSplineObject(object)
        || isSketchStyleSplineObject(object);
}

/**
 * 收集点击可拾取对象。
 * @param scene 场景对象。
 * @returns 候选对象列表。
 */
export function collectClickSelectionTargets(scene: THREE.Scene): THREE.Object3D[] {
    const candidates: THREE.Object3D[] = [];
    traverseEntitySelectionTree(scene, (object) => {
        if (isEntitySelectionCandidateObject(object) && isEntitySelectionCandidateEligible(object)) {
            candidates.push(object);
        }
    });
    return candidates;
}

/**
 * 收集悬浮可拾取对象。
 * @param scene 场景对象。
 * @returns 候选对象列表。
 */
export function collectHoverSelectionTargets(scene: THREE.Scene): THREE.Object3D[] {
    const candidates: THREE.Object3D[] = [];
    traverseEntitySelectionTree(scene, (object) => {
        if (isEntitySelectionCandidateObject(object) && isEntitySelectionCandidateEligible(object)) {
            candidates.push(object);
        }
    });
    return candidates;
}

/**
 * 收集框选候选对象。
 * @param scene 场景对象。
 * @returns 候选对象列表。
 */
export function collectBoxSelectionTargets(scene: THREE.Scene): THREE.Object3D[] {
    const candidates: THREE.Object3D[] = [];
    traverseEntitySelectionTree(scene, (object) => {
        if (
            isEntitySelectionCandidateEligible(object)
            && isBoxSelectionCandidateObject(object)
        ) {
            candidates.push(object);
        }
    });
    return candidates;
}

/**
 * 将实体对象数组转换为命中结果数组。
 * @param objects 实体对象数组。
 * @returns 命中结果数组。
 */
export function toIntersections(objects: THREE.Object3D[]): THREE.Intersection<THREE.Object3D>[] {
    return objects.map((object) => ({
        distance: 0,
        object,
        point: object.getWorldPosition(new THREE.Vector3()),
    }));
}

/**
 * 判断对象是否属于实体 mesh 命中结果。
 *
 * @param object 目标对象。
 * @returns 是否为实体 mesh 命中。
 */
function isFeatureMeshSelectionHitObject(object: THREE.Object3D): object is FeatureFace | FeatureResultMesh {
    return isFeatureFace(object) || isFeatureResultMesh(object);
}

/**
 * 解析前景面的最佳边界线命中。
 *
 * 设计说明：
 * 1. 同一前景面上可能同时有多条边界线落入屏幕命中带，不能继续只按“全局最近线段”选边；
 * 2. 当 Raycaster 已经给出当前前景面的交点后，更符合 CAD 语义的做法是比较“面交点到边界线的三维距离”；
 * 3. 这样可以稳定地区分“点上边”与“点同面上的其他边”，避免上边线被侧边抢走。
 *
 * @param faceHit 当前前景面命中。
 * @param lineHits 当前前景面边界线命中集合。
 * @returns 当前最佳边界线命中；不存在时返回 `null`。
 */
function resolveFeatureFaceBoundaryLineHit(
    faceHit: THREE.Intersection<FeatureFace> | null,
    lineHits: Array<THREE.Intersection<THREE.Object3D> & { screenDistance: number }>,
): (THREE.Intersection<THREE.Object3D> & { screenDistance: number }) | null {
    if (!faceHit || lineHits.length === 0) {
        return null;
    }

    const facePoint = faceHit.point;
    return lineHits.reduce<(THREE.Intersection<THREE.Object3D> & { screenDistance: number }) | null>((bestHit, currentHit) => {
        if (!bestHit) {
            return currentHit;
        }

        const currentWorldDistance = currentHit.point.distanceTo(facePoint);
        const bestWorldDistance = bestHit.point.distanceTo(facePoint);
        if (currentWorldDistance < bestWorldDistance - 1e-6) {
            return currentHit;
        }
        if (Math.abs(currentWorldDistance - bestWorldDistance) <= 1e-6) {
            if (currentHit.screenDistance < bestHit.screenDistance - 1e-6) {
                return currentHit;
            }
            if (
                Math.abs(currentHit.screenDistance - bestHit.screenDistance) <= 1e-6
                && currentHit.distance < bestHit.distance
            ) {
                return currentHit;
            }
        }
        return bestHit;
    }, null);
}

/**
 * 解析当前应视为同一前景层的面命中集合。
 *
 * @param faceHits 所有面命中。
 * @returns 同一前景层的面命中集合。
 */
function resolveForegroundFeatureFaceHits(
    faceHits: Array<THREE.Intersection<FeatureFace>>,
): Array<THREE.Intersection<FeatureFace>> {
    if (faceHits.length === 0) {
        return [];
    }
    const nearestDistance = faceHits[0]!.distance;
    return faceHits.filter((faceHit) => Math.abs(faceHit.distance - nearestDistance) <= 1e-3);
}

/**
 * 收集前景面集合的唯一边界线。
 *
 * @param faceHits 前景面命中集合。
 * @returns 去重后的边界线集合。
 */
function collectForegroundBoundaryLines(
    faceHits: Array<THREE.Intersection<FeatureFace>>,
): FeatureLine[] {
    const boundaryLines = new Set<FeatureLine>();
    faceHits.forEach((faceHit) => {
        faceHit.object.getBoundaryLines().forEach((boundaryLine) => {
            boundaryLines.add(boundaryLine);
        });
    });
    return [...boundaryLines];
}

/**
 * 按活动草图优先规则从候选点命中集合中选一个最终命中。
 *
 * 设计说明：
 * 1. 本函数承担"重合 foreign point 去噪 + 活动草图点优先"的领域裁决；picker 只给候选集合；
 * 2. 规则：
 *    - 无活动草图 (`activeSketchGroupId === null`) → 返回 `hits[0]`（picker 已排序的最近点），行为等价旧 `pickNearestPoint`；
 *    - 有活动草图：
 *      - 将候选拆成"属于活动草图"与"属于其他草图 / 无草图归属"两组；
 *      - 若活动草图组非空 → 返回活动草图候选的最近点（foreign 被整体 subsume，等同于"与活动草图点重合的 foreign point 去噪"）；
 *      - 若活动草图组为空 → 返回 `hits[0]`（退化为旧行为，避免过度拦截）；
 * 3. 候选集合由 picker 按 (screenDistance, rayDistance) 排序；本函数不再重新排序，只过滤；
 * 4. "结构性点语义" (`hasSharedSketchEntity`) 在本版不单独实现——当前规则已能覆盖大多数重合场景。
 *
 * @param input 输入。
 * @returns 单个命中结果；无命中返回 null。
 */
function resolveActiveSketchPrioritizedPointHit(input: {
    hits: THREE.Intersection<THREE.Object3D>[];
    activeSketchGroupId: string | null;
}): THREE.Intersection<THREE.Object3D> | null {
    if (input.hits.length === 0) {
        return null;
    }
    if (input.activeSketchGroupId === null) {
        return input.hits[0];
    }
    const activeSketchHits = input.hits.filter(
        (hit) => findAncestorSketchGroupId(hit.object) === input.activeSketchGroupId,
    );
    if (activeSketchHits.length > 0) {
        return activeSketchHits[0];
    }
    return input.hits[0];
}

/**
 * 判断实体候选是否应在当前对象选择模式下退出交互。
 *
 * 设计说明：
 * 特征替换预览已经由黄色预览结果表达当前实体状态，源实体在对象模式下不能继续抢占 hover；
 * 边、面、点等拓扑模式仍必须保留源拓扑宿主，以支持圆角、倒角等命令继续吸纳同实体引用。
 *
 * @param runtime 选择运行时。
 * @param object 候选场景对象。
 * @returns 当前候选应退出对象模式拾取时返回 true。
 */
/**
 * 判断 Raycaster 命中是否仍位于剖面视图的可见半空间。
 *
 * 设计说明：WebGPU `ClippingGroup` 只改变像素输出，不会自动裁剪 CPU Raycaster 结果；
 * 这里统一委托 Viewer 的剖面策略，避免面、边、点各自形成不同的“看不见但能选中”行为。
 *
 * @param runtime 选择管理器运行时。
 * @param intersection 世界空间命中。
 * @returns 命中仍可见时返回 true。
 */
export function isIntersectionVisibleAfterSection(
    runtime: Pick<EntitySelectionManagerRuntime, 'renderer'>,
    intersection: THREE.Intersection<THREE.Object3D>,
): boolean {
    return runtime.renderer.isSectionPointVisible?.(intersection.point) ?? true;
}

/**
 * 根据屏幕坐标拾取实体。
 * @param runtime 选择管理器运行时上下文。
 * @param clientX 屏幕 X 坐标。
 * @param clientY 屏幕 Y 坐标。
 * @param interactableObjects 可参与拾取的对象列表。
 * @returns 命中实体，未命中返回 null。
 */
export function pickEntityAt(
    runtime: EntitySelectionManagerRuntime,
    clientX: number,
    clientY: number,
    interactableObjects: THREE.Object3D[],
    selectionModeOverride?: EntitySelectionMode,
): THREE.Intersection<THREE.Object3D> | null {
    const container = runtime.renderer.container;
    const camera = runtime.renderer.camera;
    if (!container || !camera) {
        return null;
    }

    const rect = container.getBoundingClientRect();
    runtime.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    runtime.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    runtime.raycaster.setFromCamera(runtime.mouseNdc, camera);

    if (interactableObjects.length === 0) {
        return null;
    }

    const selectionMode = selectionModeOverride ?? runtime.selectionMode;
    const {
        sketchMeshCandidates,
        featureMeshCandidates,
        sketchLineCandidates,
        featureLineCandidates,
        sketchPointCandidates,
        featurePointCandidates,
    } = runtime.entitySelectionQueryPartitioner.partition(
        interactableObjects,
        selectionMode,
        runtime.isModelBodyPreviewObjectPickBlocked,
    );

    // 两个通道独立比较完整求交成本，不影响屏幕点线队列与拓扑优先级。
    const sketchMeshIntersects = runtime.renderer.intersectMeshRaycastCandidates
        ? runtime.renderer.intersectMeshRaycastCandidates(sketchMeshCandidates, runtime.raycaster, runtime, 'sketch')
        : runtime.raycaster.intersectObjects(sketchMeshCandidates, false);
    const normalizedSketchMeshIntersects = sketchMeshIntersects
        .map((item) => normalizeIntersectionObject(item))
        .filter((item): item is THREE.Intersection<SketchContour | RevolveRegionContourObject> => (
            isSketchContour(item.object) || isRevolveRegionContourObject(item.object)
        ));
    const featureMeshIntersects = runtime.renderer.intersectMeshRaycastCandidates
        ? runtime.renderer.intersectMeshRaycastCandidates(featureMeshCandidates, runtime.raycaster, runtime, 'feature')
        : runtime.raycaster.intersectObjects(featureMeshCandidates, false);
    const normalizedFeatureMeshIntersects = featureMeshIntersects
        .filter((item) => isIntersectionVisibleAfterSection(runtime, item))
        .map((item) => normalizeIntersectionObject(item))
        .filter((item): item is THREE.Intersection<FeatureFace | FeatureResultMesh> => isFeatureMeshSelectionHitObject(item.object));
    const sketchContourHit = normalizedSketchMeshIntersects[0] ?? null;
    const meshHit = normalizedFeatureMeshIntersects[0] ?? null;
    const faceHits = normalizedFeatureMeshIntersects.filter((item): item is THREE.Intersection<FeatureFace> => isFeatureFace(item.object));
    const foregroundFaceHits = resolveForegroundFeatureFaceHits(faceHits);
    const faceHit = foregroundFaceHits[0] ?? null;

    const featureLinePickCandidates = resolveFeatureLinePickCandidates({
        selectionMode,
        faceHits: foregroundFaceHits,
        featureLineCandidates,
        allowOccludedFeatureTopologySelection: runtime.allowOccludedFeatureTopologySelection === true,
    });

    const sketchPointHits = runtime.screenSpacePrecisionPicker.collectPointHits({
        points: sketchPointCandidates,
        camera,
        containerRect: rect,
        clientX,
        clientY,
        ray: runtime.raycaster.ray,
    });
    const activeSketchGroupId = runtime.renderer.getActiveSketchGroupId?.() ?? null;
    const sketchPointHit = resolveActiveSketchPrioritizedPointHit({
        hits: sketchPointHits,
        activeSketchGroupId,
    });
    const featurePointHit = runtime.screenSpacePrecisionPicker.collectPointHits({
        points: featurePointCandidates,
        camera,
        containerRect: rect,
        clientX,
        clientY,
        ray: runtime.raycaster.ray,
    }).find((item) => isIntersectionVisibleAfterSection(runtime, item)) ?? null;
    const sketchLineHit = runtime.screenSpacePrecisionPicker.pickNearestLine({
        lines: sketchLineCandidates,
        camera,
        containerRect: rect,
        clientX,
        clientY,
        ray: runtime.raycaster.ray,
    });
    const featureLineHits = runtime.screenSpacePrecisionPicker.collectLineHits({
        lines: featureLinePickCandidates,
        camera,
        containerRect: rect,
        clientX,
        clientY,
        ray: runtime.raycaster.ray,
    }).filter((item) => isIntersectionVisibleAfterSection(runtime, item));
    const featureLineHit = featureLineHits.reduce<THREE.Intersection<THREE.Object3D> | null>((bestHit, currentHit) => {
        if (!bestHit) {
            return currentHit;
        }
        const bestScreenDistance = Reflect.get(bestHit, 'screenDistance') as number;
        if (currentHit.screenDistance < bestScreenDistance - 1e-9) {
            return currentHit;
        }
        if (
            Math.abs(currentHit.screenDistance - bestScreenDistance) <= 1e-9
            && currentHit.distance < bestHit.distance
        ) {
            return currentHit;
        }
        return bestHit;
    }, null);
    const faceBoundaryLineHit = faceHit
        ? resolveFeatureFaceBoundaryLineHit(
            faceHit,
            runtime.screenSpacePrecisionPicker.collectLineHits({
                lines: collectForegroundBoundaryLines(foregroundFaceHits),
                camera,
                containerRect: rect,
                clientX,
                clientY,
                ray: runtime.raycaster.ray,
            }).filter((item) => isIntersectionVisibleAfterSection(runtime, item)),
        )
        : null;
    return resolvePreferredSelectionPick({
        sketchPointHit,
        sketchLineHit,
        sketchContourHit,
        featurePointHit,
        featureLineHit,
        faceHit,
        faceBoundaryLineHit,
        meshHit,
        selectionMode,
        // 草图编辑态下，落在草图点/线上的精确屏幕命中不应被恰好更靠前的实体面/基准面遮挡抢走。
        preferActiveSketchPreciseHit: activeSketchGroupId !== null,
    });
}

/**
 * 根据当前 Viewer 模式解析最终应采用的命中对象。
 *
 * 设计说明：
 * 1. 草图候选与实体候选必须先各自独立求得主命中，禁止在命中阶段互相过滤；
 * 2. 最终裁决只看射线距离；当草图与实体距离相同或极接近时，统一由草图优先；
 * 3. 该语义与是否处于草图编辑态无关，只要草图几何可见且命中，就允许参与同距离优先裁决。
 *
 * @param input 命中候选输入。
 * @returns 最终选中的命中对象；全部未命中时返回 null。
 */
export function resolvePreferredSelectionPick(input: {
    sketchPointHit: THREE.Intersection<THREE.Object3D> | null;
    sketchLineHit: THREE.Intersection<THREE.Object3D> | null;
    sketchContourHit: THREE.Intersection<THREE.Object3D> | null;
    featurePointHit: THREE.Intersection<THREE.Object3D> | null;
    featureLineHit: THREE.Intersection<THREE.Object3D> | null;
    faceHit: THREE.Intersection<FeatureFace> | null;
    faceBoundaryLineHit: THREE.Intersection<THREE.Object3D> | null;
    meshHit: THREE.Intersection<THREE.Object3D> | null;
    selectionMode?: EntitySelectionMode;
    /** 草图编辑态：草图点/线的精确屏幕命中优先于仅在射线深度上更靠前的实体命中。 */
    preferActiveSketchPreciseHit?: boolean;
}): THREE.Intersection<THREE.Object3D> | null {
    const sketchHit = resolvePrimarySketchHit({
        pointHit: input.sketchPointHit,
        lineHit: input.sketchLineHit,
        contourHit: input.sketchContourHit,
    });
    const entityHit = resolvePrimaryEntityHit({
        pointHit: input.featurePointHit,
        lineHit: input.featureLineHit,
        faceBoundaryLineHit: input.faceBoundaryLineHit,
        faceHit: input.faceHit,
        meshHit: input.meshHit,
        selectionMode: input.selectionMode,
    });

    /*
     * 草图编辑态的深度让位：草图平面常贴在实体面上或位于其后，若继续只按射线距离裁决，
     * 用户想抓的草图点/线端点会被恰好凸在前面的实体面/基准面稳定抢走（实测圆柱面遮挡端点）。
     * 仅当草图主命中来自 point/line 的精确屏幕拾取（携带 screenDistance）时让草图域直接胜出；
     * 轮廓/网格命中不在此列，避免误伤“草图内点选实体面做参考”等场景。
     */
    const isPreciseSketchScreenHit = sketchHit !== null
        && (sketchHit === input.sketchPointHit || sketchHit === input.sketchLineHit)
        && typeof Reflect.get(sketchHit, 'screenDistance') === 'number';

    return resolveSelectionDomainWinner({
        sketchHit,
        entityHit,
        distanceEpsilon: input.preferActiveSketchPreciseHit === true && isPreciseSketchScreenHit
            ? Number.POSITIVE_INFINITY
            : undefined,
    });
}

/**
 * 解析当前应参与实体边拾取的候选集合。
 *
 * 设计说明：
 * 1. 当光标下已经存在前景面命中时，可见边应只从该前景面的边界线里竞争；
 * 2. 这样可以从 cad-core 选择底层直接隔离被遮挡边，避免侧边/后边越过当前可见边抢选；
 * 3. 只有在没有前景面命中的情况下，才回退到全局 FeatureLine 候选，兼容开口边或背景边界场景。
 *
 * @param input 解析输入。
 * @returns 当前应参与边拾取的特征边集合。
 */
function resolveFeatureLinePickCandidates(input: {
    selectionMode: EntitySelectionMode;
    faceHits: Array<THREE.Intersection<FeatureFace>>;
    featureLineCandidates: FeatureLine[];
    allowOccludedFeatureTopologySelection: boolean;
}): FeatureLine[] {
    /**
     * `face-edge` 模式下的前景面边界线只应作为兜底提示，不能在候选收集阶段裁掉全局权威边。
     * 否则共享边点击会被当前 `faceHit` 锁死到该面的边界集合里，后续仲裁再也看不到真实全局边命中。
     */
    if (!isEdgeAwareSelectionMode(input.selectionMode)) {
        return input.featureLineCandidates;
    }
    if (input.allowOccludedFeatureTopologySelection || input.faceHits.length === 0) {
        return input.featureLineCandidates;
    }

    const boundaryLines = collectForegroundBoundaryLines(input.faceHits);
    return boundaryLines.length > 0 ? boundaryLines : input.featureLineCandidates;
}

/**
 * 判断对象是否允许在当前选择模式下参与拾取。
 *
 * 设计说明：
 * 1. 选择模式属于底层拾取约束，不应由各命令宿主在 UI 层自行过滤；
 * 2. `face-edge` 模式专门服务实体/草图的面边混合选择，不允许顶点进入；
 * 3. `topology` 模式服务装配配合，SOLIDWORKS Mate 需要同时吸收点、线、面。
 * 4. `object` 模式保持现有默认语义，不改变其它未迁移命令的行为。
 *
 * @param object 场景对象。
 * @param mode 当前选择模式。
 * @returns 是否允许参与拾取。
 */
export function isObjectAllowedInSelectionMode(
    object: THREE.Object3D,
    mode: EntitySelectionMode,
): boolean {
    if (mode === 'object') {
        return true;
    }
    return isEntitySelectionCandidateMaskAllowed(
        resolveEntitySelectionCandidateMask(object),
        mode,
    );
}

/**
 * 归一化命中对象，避免命中子对象时类型判断失效。
 * @param intersection 原始命中结果。
 * @returns 归一化后的命中结果。
 */
export function normalizeIntersectionObject(
    intersection: THREE.Intersection<THREE.Object3D>
): THREE.Intersection<THREE.Object3D> {
    if (shouldIgnoreInEntitySelection(intersection.object)) {
        return intersection;
    }

    const pointOwner = findAncestorByGuard(intersection.object, isSketchPointObject);
    if (pointOwner) {
        return {...intersection, object: pointOwner};
    }

    const featurePointOwner = findAncestorByGuard(intersection.object, isFeaturePoint);
    if (featurePointOwner) {
        return {...intersection, object: featurePointOwner};
    }

    const lineOwner = findAncestorByGuard(intersection.object, isSketchLineObject);
    if (lineOwner) {
        return {...intersection, object: lineOwner};
    }

    const featureLineOwner = findAncestorByGuard(intersection.object, isFeatureLine);
    if (featureLineOwner) {
        return {...intersection, object: featureLineOwner};
    }

    const featureFaceOwner = findAncestorByGuard(intersection.object, isFeatureFace);
    if (featureFaceOwner) {
        return {...intersection, object: featureFaceOwner};
    }

    //------ 圆椭圆轮廓拖拽归一化 开始------
    // 圆/椭圆的轮廓 Mesh 挂在源曲线对象下面，必须先保留真实命中的轮廓对象，否则后续会被父级圆/椭圆吞掉。
    const contourOwner = findAncestorByGuard(intersection.object, isSketchContour);
    if (contourOwner) {
        return {...intersection, object: contourOwner};
    }
    //------ 圆椭圆轮廓拖拽归一化 结束------

    const circleOwner = findAncestorByGuard(intersection.object, isSketchCircleObject);
    if (circleOwner) {
        return {...intersection, object: circleOwner};
    }

    const arcOwner = findAncestorByGuard(intersection.object, isSketchArcObject);
    if (arcOwner) {
        return {...intersection, object: arcOwner};
    }

    const ellipseOwner = findAncestorByGuard(intersection.object, isSketchEllipseObject);
    if (ellipseOwner) {
        return {...intersection, object: ellipseOwner};
    }

    const revolveRegionOwner = findAncestorByGuard(intersection.object, isRevolveRegionContourObject);
    if (revolveRegionOwner) {
        return {...intersection, object: revolveRegionOwner};
    }

    const segmentGeometryOwner = findAncestorByGuard(intersection.object, isSketchCollectionObject);
    if (segmentGeometryOwner) {
        return {...intersection, object: segmentGeometryOwner};
    }

    const featureMeshOwner = findAncestorByGuard(intersection.object, isFeatureResultMesh);
    if (featureMeshOwner) {
        if (typeof intersection.faceIndex === 'number') {
            const featureFace = featureMeshOwner.getFeatureFaceByTriangleIndex(intersection.faceIndex);
            if (featureFace) {
                return {...intersection, object: featureFace};
            }
        }
        return {...intersection, object: featureMeshOwner};
    }

    return intersection;
}

//------ 移动实体混合边轮廓选择 开始------
/**
 * 判断对象是否是轮廓可联动的草图实体。
 *
 * 设计说明：
 * 1. 闭合轮廓可能由直线、圆弧、样条、抛物线等混合边组成；
 * 2. 轮廓选择不能只保留直线，否则移动实体等命令会漏掉圆弧；
 * 3. 圆和椭圆虽然不是普通边对象，但自闭合轮廓需要保留其实体语义。
 *
 * @param object 待判断对象。
 * @returns 是轮廓可联动草图实体时返回 true。
 */
function isContourLinkedSketchEntity(object: THREE.Object3D): boolean {
    return isAbstractSketchEdgeObject(object)
        || isSketchLineObject(object)
        || isSketchCircleObject(object)
        || isSketchEllipseObject(object)
        || isSketchSlotObject(object);
}

/**
 * 从轮廓对象解析其关联草图实体列表。
 *
 * @param contour 轮廓对象。
 * @returns 关联草图实体列表。
 */
export function resolveContourLinkedSketchEntities(contour: SketchContour): THREE.Object3D[] {
    const linkedEntities = new Map<string, THREE.Object3D>();

    /**
     * 追加单个轮廓关联实体。
     *
     * @param object 候选草图对象。
     */
    const appendLinkedEntity = (object: THREE.Object3D | null | undefined): void => {
        if (!object || !isContourLinkedSketchEntity(object) || linkedEntities.has(object.uuid)) {
            return;
        }

        linkedEntities.set(object.uuid, object);
    };

    // 真实轮廓对象优先使用统一维护的关联实体；旧快照或轻量测试替身缺少该方法时继续走 source/children 兜底。
    if (typeof contour.getLinkObjects === 'function') {
        contour.getLinkObjects().forEach(appendLinkedEntity);
    }
    if (linkedEntities.size > 0) {
        return Array.from(linkedEntities.values());
    }

    const contourSource = contour.source;
    appendLinkedEntity(contourSource);

    if (isSketchCollectionObject(contourSource)) {
        contourSource.collectSet.forEach(appendLinkedEntity);
    }

    if (isSketchSlotObject(contourSource)) {
        contourSource.getEdges().forEach(appendLinkedEntity);
    }

    contour.children.forEach(appendLinkedEntity);
    return Array.from(linkedEntities.values());
}
//------ 移动实体混合边轮廓选择 结束------

/**
 * 从轮廓对象解析其关联的线对象列表。
 * @param contour 轮廓对象。
 * @returns 关联线对象列表。
 */
export function resolveContourLinkedLines(contour: SketchContour): SketchLineObject[] {
    return resolveContourLinkedSketchEntities(contour)
        .filter((object): object is SketchLineObject => isSketchLineObject(object));
}

/**
 * 判断对象是否属于允许参与选择的状态对象。
 * @param object 待判断对象。
 * @returns 是否为允许参与选择的对象。
 */
export function isSketchStateObject(object: THREE.Object3D): object is
    | FeaturePoint
    | FeatureLine
    | FeatureFace
    | FeatureResultMesh
    | RevolveRegionContourObject
    | SketchContour
    | SketchCollectionObject
    | import('../../mesh/sketch').SketchPointObject
    | import('../../mesh/sketch').SketchLineObject
    | import('../../mesh/sketch').SketchCircleObject
    | import('../../mesh/sketch').SketchArcObject
    | import('../../mesh/sketch').SketchEllipseObject
    | import('../../mesh/sketch').SketchSplineObject
    | import('../../mesh/sketch').SketchStyleSplineObject {
    // 会话临时参考点只服务点捕捉，即使调用方直接提交对象也不能进入统一选择状态。
    if (isSketchPointObject(object) && !isSketchPointEntitySelectable(object)) {
        return false;
    }
    return ENTITY_SELECTION_STATE_OBJECT_GUARDS.some((guard) => guard(object));
}

/**
 * 向上查找满足守卫条件的父级对象。
 * @param object 起始对象。
 * @param guard 类型守卫。
 * @returns 命中的父级对象；未命中返回 null。
 */
export function findAncestorByGuard<T extends THREE.Object3D>(
    object: THREE.Object3D,
    guard: (value: THREE.Object3D) => value is T
): T | null {
    let current: THREE.Object3D | null = object;
    while (current) {
        if (guard(current)) {
            return current;
        }
        current = current.parent;
    }
    return null;
}
